import { useCallback, useState } from "react";
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuIcon,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  DateRangeFilterMenuItemType,
  DateOperator,
  dateOperatorToLabel,
} from "../FilterMenu.model";
import {
  isBetweenFilter,
  isGreaterThanOrEqualFilter,
  isLessThanOrEqualFilter,
} from "../FieldFilter.model";
import { DateRangeCalendar } from "../DateRangeCalendar";
import { DateSingleCalendar } from "../DateSingleCalendar";

function getOperator(
  value: DateRangeFilterMenuItemType<string>["value"]
): DateOperator {
  if (value == null) return "between";
  if (isBetweenFilter(value)) return "between";
  if (isGreaterThanOrEqualFilter(value)) return "gte";
  if (isLessThanOrEqualFilter(value)) return "lte";
  return "between";
}

interface DateRangeFilterMenuItemProps<Field extends string>
  extends DateRangeFilterMenuItemType<Field> {
  onChange: (item: DateRangeFilterMenuItemType<Field>) => void;
  onRequestClose?: () => void;
}

export function DateRangeFilterMenuItem<Field extends string>({
  onChange,
  onRequestClose,
  ...item
}: DateRangeFilterMenuItemProps<Field>) {
  const [operator, setOperator] = useState<DateOperator>(() =>
    getOperator(item.value)
  );

  const range =
    item.value != null && isBetweenFilter(item.value)
      ? item.value.value
      : undefined;

  const singleDate =
    item.value != null &&
    (isGreaterThanOrEqualFilter(item.value) ||
      isLessThanOrEqualFilter(item.value))
      ? (item.value.value as number)
      : undefined;

  const handleRangeChange = useCallback(
    (from: number, to: number) => {
      onChange({
        ...item,
        value: { operator: "between", value: [from, to] },
      });
      onRequestClose?.();
    },
    [item, onChange, onRequestClose]
  );

  const handleSingleChange = useCallback(
    (date: number) => {
      onChange({
        ...item,
        value: { operator: operator as "gte" | "lte", value: date },
      });
      onRequestClose?.();
    },
    [item, onChange, onRequestClose, operator]
  );

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        {item.icon && <DropdownMenuIcon>{item.icon}</DropdownMenuIcon>}
        {item.label}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent
        sideOffset={8}
        alignOffset={-5}
        className="w-auto p-0"
      >
        <div className="px-1 pt-1">
          {(["between", "gte", "lte"] as const).map((op) => (
            <DropdownMenuCheckboxItem
              key={op}
              checked={operator === op}
              onCheckedChange={() => setOperator(op)}
              onSelect={(e) => e.preventDefault()}
            >
              {dateOperatorToLabel[op]}
            </DropdownMenuCheckboxItem>
          ))}
        </div>
        <DropdownMenuSeparator />
        {operator === "between" ? (
          <DateRangeCalendar
            from={range?.[0]}
            to={range?.[1]}
            onChange={handleRangeChange}
          />
        ) : (
          <DateSingleCalendar
            date={singleDate}
            bound={operator === "lte" ? "end" : "start"}
            onChange={handleSingleChange}
          />
        )}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
